import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { AuthProvider } from './auth/AuthContext'
import { initKeycloak } from './auth/keycloak'
import { loadRuntimeConfig } from './runtimeConfig'
import { Shell } from './components/Shell'
import { Rooms } from './screens/Rooms'
import { Room } from './screens/Room'
import { People } from './screens/People'
import { Questions } from './screens/Questions'
import { Checklist } from './screens/Checklist'
import { Activity } from './screens/Activity'
import { Permissions } from './screens/Permissions'
import { Settings } from './screens/Settings'
import { Organization } from './screens/Organization'
import { RoomLayout } from './components/RoomLayout'
import { NotFound } from './screens/NotFound'
import { InviteAccept } from './screens/InviteAccept'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: 1,
    },
  },
})

const router = createBrowserRouter([
  {
    path: '/',
    element: <Shell />,
    children: [
      { index: true, element: <Rooms /> },
      {
        path: 'rooms/:roomId',
        element: <RoomLayout />,
        children: [
          { index: true, element: <Room /> },
          { path: 'questions', element: <Questions /> },
          { path: 'checklist', element: <Checklist /> },
          { path: 'activity', element: <Activity /> },
          { path: 'people', element: <People /> },
          { path: 'permissions', element: <Permissions /> },
          { path: 'settings', element: <Settings /> },
        ],
      },
      // The organization's owners: its data policy and who accepted it (ADR 0008). The API decides who may be here.
      { path: 'organizations/:organizationId', element: <Organization /> },
      { path: '*', element: <NotFound /> },
    ],
  },
])

/*
  Authenticate BEFORE rendering. keycloak-js redirects to the login page during init
  when the session is absent, so an unauthenticated user never reaches React at all --
  no flash of application chrome, no window for an unauthenticated request.
*/
async function bootstrap() {
  // Before anything else, including Keycloak: the realm and client id come from
  // /config.json, and the Keycloak client cannot be constructed without them.
  await loadRuntimeConfig()

  const root = document.getElementById('root')!

  /*
    The one page that renders WITHOUT a session: the invitation link. The person has no
    account to sign in with until they accept, so Keycloak is not initialised here at
    all -- login-required would bounce them to a login they cannot pass. The token in
    the path is the credential, and the API rate-limits and single-uses it.
  */
  const invite = /^\/invite\/([A-Za-z0-9_-]{43})\/?$/.exec(window.location.pathname)
  if (invite?.[1]) {
    createRoot(root).render(
      <StrictMode>
        <InviteAccept token={invite[1]} />
      </StrictMode>,
    )
    return
  }

  try {
    await initKeycloak()
  } catch (error) {
    // Deliberately unbranded: there is no token and no stylesheet worth waiting for.
    // Classes, not style attributes -- the CSP has no 'unsafe-inline'.
    root.replaceChildren()
    const box = document.createElement('div')
    box.className = 'fatal'
    const inner = document.createElement('div')
    const title = document.createElement('p')
    title.className = 'fatal-title'
    title.textContent = 'Could not reach the identity provider.'
    const detail = document.createElement('p')
    detail.className = 'fatal-detail'
    detail.textContent = String(error)
    inner.append(title, detail)
    box.append(inner)
    root.append(box)
    return
  }

  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </QueryClientProvider>
    </StrictMode>,
  )
}

void bootstrap()
